import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Box, Flex, Link as ExternalLink } from 'rebass/styled-components'
import { Link } from 'gatsby'
import styled from 'styled-components'
import { modalToggle } from '../../system/redux/reducers/modal'
import MediaRender from './MediaRender'
import { useLocation } from '@reach/router'

const Container = styled(Box)`
    span {
        display: block;
        &.headline {
            background-image: linear-gradient(to right, #000 0, #000 100%);
            background-position: 0 100%;
            background-repeat: no-repeat;
            background-size: 0 2px;
            display: inline;
            will-change: background-size;
        }
    }

    a {
        color: ${(props) => props.theme.colors.black};
        text-decoration: none;

        &:hover .headline {
            background-size: 100% 2px;
            transition-duration: 400ms, 250ms;
            transition-property: background-size;
            transition-timing-function: cubic-bezier(0.2, 0.75, 0.5, 1);
        }
    }

    .pdf {
        cursor: pointer;
        &:hover .headline {
            background-size: 100% 2px;
            transition-duration: 400ms, 250ms;
            transition-property: background-size;
            transition-timing-function: cubic-bezier(0.2, 0.75, 0.5, 1);
        }
    }

    &.dark {
        a {
            color: ${(props) => props.theme.colors.white};
        }
        span {
            &.headline {
                background-image: linear-gradient(to right, #fff 0, #fff 100%);
            }
        }
    }
`

const Badge = styled(Box)`
    align-items: center;
    border-radius: 100%;
    color: ${(props) => props.theme.colors.aquaLight};
    display: flex;
    font-family: ${(props) => props.theme.fonts.heading};
    font-size: ${(props) => props.theme.fontSizes.xl};
    height: 82px;
    justify-content: center;
    line-height: 22px;
    position: absolute;
    right: 6px;
    text-align: center;
    top: -43px;
    width: 82px;
    z-index: 1;
    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 14px;
        height: 64px;
        line-height: 18px;
        top: -33px;
        width: 64px;
    }
`
const PlayButton = styled(Flex)`
    position: absolute;
    background-color: ${(props) => props.theme.colors.white};
    border-radius: 100%;
    color: ${(props) => props.theme.colors.aquaLight};
    height: 58px;
    left: calc(50% - 29px);
    opacity: 0.8;
    top: calc(50% - 29px);
    width: 58px;
    z-index: 1;
    justify-content: center;
    align-items: center;
    will-change: transform;

    &:before {
        content: '';
        border-color: transparent transparent transparent
            ${(props) => props.theme.colors.aquaDark};
        border-style: solid;
        border-width: 27px 0 27px 44px;
        box-sizing: border-box;
        height: 44px;
        margin-left: 4px;
        transform: scale(0.5);
        transform-origin: 50% 50%;
        width: 44px;
        z-index: 1;
        display: block;
        will-change: transform;
        @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
            transform: scale(0.4);
        }
    }

    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        transform: scale(0.8);
    }
`
const Headline = styled(Box)`
    display: inline;
    font-family: ${(props) => props.theme.fonts.heading};
    font-weight: ${(props) => props.theme.fontWeights.semibold};
    font-size: ${(props) => props.theme.fontSizes.h2};
    letter-spacing: 4px;
    line-height: 34px;
    text-transform: uppercase;

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        font-size: ${(props) => props.theme.fontSizes.h4};
        letter-spacing: auto;
        line-height: 26px;
    }
`

const FeedItem = ({ dispatch, children, options, onPress }) => {
    const [item, setItem] = useState(null)
    const location = useLocation()

    const openPublication = (pdfLink) => {
        const content = {
            type: 'publication',
            title: options.headline,
            description: options.content && options.content.description,
            item: { module: 'PdfFlipper', props: { pdf: pdfLink } },
        }
        const modalData = JSON.stringify({ isOpen: true, content })
        dispatch(modalToggle(modalData))
    }

    useEffect(() => {
        let badge = null
        let play = null
        if (options.categories?.length) {
            options.saleProfileStatus = options.categories
        }
        if (options.saleProfileStatus && options.saleProfileStatus.length) {
            if (options.saleProfileStatus.includes('new-entry')) {
                badge = <Badge bg='aquaDark'>New Entry</Badge>
            } else if (options.saleProfileStatus.includes('under-offer')) {
                badge = <Badge bg='black'>Under Offer</Badge>
            } else if (options.saleProfileStatus.includes('sale-agreed')) {
                badge = <Badge bg='black'>Sale Agreed</Badge>
            } else if (location.pathname == '/' && options.saleProfileStatus.includes('available')) {
                badge = <Badge bg='aquaDark'>Available</Badge>
            } else if (options.saleProfileStatus.includes('recently-sold')) {
                badge = <Badge bg='black'>Recently Sold</Badge>
            } else if (options.saleProfileStatus.includes('sold')) {
                badge = <Badge bg='black'>Sold</Badge>
            }
        }

        if (options.categories && options.categories.length) {
            if (options.categories.includes('films-feature')) {
                badge = (
                    <Badge bg='black'>
                        Feature
                        <br />
                        Film
                    </Badge>
                )
            }

            if (options.categories.includes('films')) {
                play = <PlayButton />
            }
        }

        const inner = (
            <>
                <Box
                    sx={{
                        paddingBottom: 'sm',
                        position: 'relative',
                    }}
                >
                    {badge}
                    {play}
                    <MediaRender
                        file={options.imageData}
                        alt={options.caption}
                        options={options}
                    />
                </Box>
                {options.showTitle ? (
                    <Headline
                        className='headline'
                        as='span'
                    >
                        {options.headline}
                    </Headline>
                ) : null}
                {options.showCaption ? (
                    <Box
                        as='span'
                        fontStyle='italic'
                        pt='xs'
                        pr='xxxl'
                    >
                        {options.caption}
                    </Box>
                ) : null}
                {children}
            </>
        )

        let output

        if (options.categories && options.categories.includes('publications')) {
            let pdfLink = '#'
            if (options.media?.gallery?.docs && options.media.gallery.docs[0]) {
                const file = options.media.gallery.docs[0]
                const baseUrl = process.env.GATSBY_CLOUDINARY_IMAGE_URL
                const filepath =
                    file.filepath +
                    file.filename.replace(/[(]/g, '_').replace(/[)]/g, '')
                pdfLink = baseUrl + filepath
            }
            output = (
                <>
                    <Box
                        className='pdf'
                        display={['none', 'none', 'none', 'none', 'block']}
                        onClick={(e) => openPublication(pdfLink)}
                    >
                        {inner}
                    </Box>
                    <Box
                        className='pdf'
                        display={['block', 'block', 'block', 'block', 'none']}
                    >
                        <ExternalLink
                            href={pdfLink}
                            target='_blank'
                        >
                            {inner}
                        </ExternalLink>
                    </Box>
                </>
            )
        } else if (options.href && typeof onPress !== 'undefined') {
            output = (
                <Link
                    to={options.href}
                    onClick={(e) =>
                        onPress(e, options.href, options.src, options.srcset)
                    }
                >
                    {inner}
                </Link>
            )
        } else {
            output = <Link to={options.href}>{inner}</Link>
        }

        setItem(output)
    }, [options, children])

    const classes = options.mode
        ? options.mode + ' ' + options.classes
        : options.classes

    return (
        <>
            <Container
                className={classes}
                width='100%'
            >
                {item}
            </Container>
        </>
    )
}

function mapStateToProps(state) {
    return {}
}

export default connect(mapStateToProps)(FeedItem)
